/**
 * Reusable upload button component with click + drag-and-drop support.
 */
import { showToast } from '../main';
import { TOAST_DROP_IMAGES_ONLY } from '../utils/app-strings';

const isTouchDevice = () => 'ontouchstart' in window || navigator.maxTouchPoints > 0;

/**
 * Returns HTML for a `.photo-upload-btn` element.
 */
export function renderUploadButton(id: string): string {
  const uploadText = isTouchDevice()
    ? 'Tap to upload photos'
    : 'Click or drag and drop to upload photos';
  return `
    <div class="photo-upload-btn" id="${id}">
      <i class="fas fa-cloud-upload-alt"></i>
      <span>${uploadText}</span>
    </div>
  `;
}

/**
 * Wire click + drag-and-drop on an existing element (by id).
 * Creates a hidden file input, attaches event listeners, returns a cleanup function.
 * The element can be a `.photo-upload-btn` or any other element (e.g. the about-tab's compact `+` tile).
 */
export function setupUploadButton(
  id: string,
  onFiles: (files: File[]) => void,
): (() => void) | undefined {
  const el = document.getElementById(id);
  if (!el) return undefined;

  // Create hidden file input
  const fileInput = document.createElement('input');
  fileInput.type = 'file';
  fileInput.accept = 'image/*';
  fileInput.multiple = true;
  fileInput.style.display = 'none';
  el.appendChild(fileInput);

  const handleFiles = (files: File[]) => {
    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    if (imageFiles.length > 0) {
      onFiles(imageFiles);
    } else {
      showToast(TOAST_DROP_IMAGES_ONLY, 'error');
    }
  };

  // Track whether the file picker is open so we can show early feedback
  // on iOS (the OS does HEIC→JPEG / iCloud download before firing `change`)
  let pickerOpen = false;

  const showProcessing = () => {
    el.innerHTML = '<div class="spinner" style="width: 24px; height: 24px;"></div><span>Processing...</span>';
    el.classList.add('uploading');
    el.appendChild(fileInput);
  };

  const restoreButton = () => {
    const uploadText = isTouchDevice()
      ? 'Tap to upload photos'
      : 'Click or drag and drop to upload photos';
    el.innerHTML = `<i class="fas fa-cloud-upload-alt"></i><span>${uploadText}</span>`;
    el.classList.remove('uploading');
    el.appendChild(fileInput);
  };

  const onClick = () => {
    pickerOpen = true;
    fileInput.click();

    // When the file picker closes, the page regains focus/visibility.
    // Show "Processing..." immediately — the `change` event may lag 1-3 s on iOS.
    const onFocusBack = () => {
      window.removeEventListener('focus', onFocusBack);
      document.removeEventListener('visibilitychange', onVisibilityBack);
      if (!pickerOpen) return;

      showProcessing();

      // If user cancelled (no files), `change` never fires.
      // Use a timeout to restore the button after a generous window.
      const cancelTimer = setTimeout(() => {
        if (pickerOpen) {
          pickerOpen = false;
          restoreButton();
        }
      }, 10000);

      // When `change` fires, clear the cancel timer
      const onceChange = () => {
        clearTimeout(cancelTimer);
        fileInput.removeEventListener('change', onceChange);
      };
      fileInput.addEventListener('change', onceChange);
    };

    const onVisibilityBack = () => {
      if (document.visibilityState === 'visible') {
        onFocusBack();
      }
    };

    window.addEventListener('focus', onFocusBack);
    document.addEventListener('visibilitychange', onVisibilityBack);
  };
  const onDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    el.classList.add('drag-over');
  };
  const onDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    el.classList.remove('drag-over');
  };
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    el.classList.remove('drag-over');
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      handleFiles(Array.from(files));
    }
  };
  const onChange = () => {
    pickerOpen = false;
    if (fileInput.files && fileInput.files.length > 0) {
      handleFiles(Array.from(fileInput.files));
      fileInput.value = '';
    } else {
      // User cancelled — restore button
      restoreButton();
    }
  };

  el.addEventListener('click', onClick);
  el.addEventListener('dragover', onDragOver);
  el.addEventListener('dragleave', onDragLeave);
  el.addEventListener('drop', onDrop);
  fileInput.addEventListener('change', onChange);

  return () => {
    el.removeEventListener('click', onClick);
    el.removeEventListener('dragover', onDragOver);
    el.removeEventListener('dragleave', onDragLeave);
    el.removeEventListener('drop', onDrop);
    fileInput.removeEventListener('change', onChange);
    fileInput.remove();
  };
}
